import React, {useMemo, useState} from 'react';
import {clsx} from 'clsx';
import {formatDateTime} from "../../utils";
import ImageBasic from "../Common/Image/ImageBasic";
import IMG from "../../constants/image";
import IconSvg from "../IconSvg/IconSvg";
import {text} from "../../constants/text";
import {isEmpty} from "lodash";
import {CONFIG_IMAGE, ICON_MOBILE_ORDER_STATUS, STATUS_ICONS, STATUS_TITLES} from "../../constants";

// Memoized status color mapping
const STATUS_COLORS = {
  order_cancel: 'bg-[#FF5B21]',
  shipping_done: 'bg-[#197D55]',
  order_refund: 'bg-[#F8C518]',
  default: 'bg-gray-100'
}

// Memoized icon color mapping
const ICON_COLORS = {
  order_cancel: 'white',
  shipping_done: 'white',
  order_refund: 'black',
  default: '#828282'
};

const StatusIcon = React.memo(({status}) => (
  <div className={clsx(
    "w-5 h-5 rounded-full flex items-center justify-center z-10",
    STATUS_COLORS[status] || STATUS_COLORS.default
  )}>
    <IconSvg
      iconName={STATUS_ICONS[status]}
      color={ICON_COLORS[status] || ICON_COLORS.default}
      size={12}
    />
  </div>
));
StatusIcon.displayName = 'StatusIcon';

const TimeDisplay = React.memo(({createdAt}) => {
  const {day, time} = formatDateTime(createdAt);
  return (
    <div className="text-gray-400 text-xs text-right">
      <div>{day}</div>
      <div>{time}</div>
    </div>
  );
});
TimeDisplay.displayName = 'TimeDisplay';

const StatusContent = React.memo(({status, message, statusEnd}) => (
  <div className="text-xs">
    <h4 className={clsx(
      (['order_cancel', 'delivered', 'returned'].includes(status) || statusEnd) ? 'text-black font-bold' : 'text-gray-900'
    )}>
      {STATUS_TITLES[status]}
    </h4>
    <p className="text-gray-500 text-[10px]">{message}</p>
  </div>
));
StatusContent.displayName = 'StatusContent';
const OrderStatusTracker = ({orderDetail}) => {
  const {statusChanges = [], status: currentStatus} = orderDetail || {};
  // Memoized calculations
  const defaultStatus = {message: '', status: 'order_created', createdAt: ''}
  // Use local storage to persist expanded state
  const [isExpanded, setIsExpanded] = useState(false);
  const [latestStatus, reversedStatuses] = useMemo(() => {
    if (isEmpty(statusChanges)) return [, []];
    const reversed = [...statusChanges].reverse();
    return [statusChanges[statusChanges.length - 1] || defaultStatus, reversed];
  }, [statusChanges]);

  // Memoize toggle handler
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="md:hidden bg-[#EEFFED] p-4">
      {/* Header */}
      <div className="flex items-start justify-between space-x-8">
        <div>
          <h3 className="text-base font-bold mb-2">{STATUS_TITLES[currentStatus] || text.waitingConfirm}</h3>
          <p className="text-gray-600">
            {latestStatus?.message || text.orderProcessing}
          </p>
        </div>
        <div className="rounded-full">
          <div className="w-12 h-12 flex items-center justify-center">
            <ImageBasic className='w-full' src={ICON_MOBILE_ORDER_STATUS[currentStatus] || CONFIG_IMAGE.MOBILE.setup}/>
          </div>
        </div>
      </div>

      {/* Expand/Collapse Button */}
      <button
        onClick={handleToggle}
        className="flex items-center justify-center mt-4 space-x-2 w-full"
      >
        {text.viewDetail}
        <ImageBasic
          className={clsx('transition-transform duration-300 w-4 ease-in-out origin-center',
            isExpanded ? 'rotate-180 ' : '')}
          src={IMG.icon.chevronDoubleDown}/>
      </button>

      {/* Timeline */}
      {isExpanded && (
        <div className="mt-4 p-4 space-y-2 bg-white rounded-xl">
          {reversedStatuses?.map((sta, index) => (
            <div key={sta.createdAt} className="flex items-start gap-4 relative">
              {index !== statusChanges.length - 1 && (
                <div
                  className="absolute left-[10px] top-4 bottom-[-25px] w-[2px] border-gray-200 border-dashed border-l-[2px]"/>
              )}
              <StatusIcon status={sta.status}/>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <StatusContent statusEnd={index === 0} status={sta.status} message={sta.message}/>
                  <TimeDisplay createdAt={sta.createdAt}/>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderStatusTracker;